import React from 'react';
import Grid from '@mui/material/Grid';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import Divider from 'components/Shared/Divider';
import StaticField from 'components/Shared/StaticField';
import { EMPTY_FIELD_VALUE } from 'helpers/constants';
import { formatCurrency, parseBrazilianNumber } from 'helpers/formats';
import { infraction } from 'services/antt';

import { CalcCard, CalcRow, NoteCard, Row, SectionSpacer, VehiclesTable } from './styles';

function FreightFloor({ infractionData }: { infractionData: infraction }) {
  const {
    origin,
    destination,
    distance,
    document_date,
    classification,
    contract_type,
    cargo_type,
    cc_coefficient,
    ccd_coefficient,
    vehicles,
    vehicle
  } = infractionData;

  const renderValue = (value?: string | null) => value || EMPTY_FIELD_VALUE;

  const formatDocumentDate = (value?: string | null) => {
    if (!value) return EMPTY_FIELD_VALUE;

    const datePart = value.trim().split(' ')[0];

    if (datePart.includes('-')) return datePart.split('-').reverse().join('/');

    return datePart;
  };

  const distanceValue = parseBrazilianNumber(distance);
  const ccd = parseBrazilianNumber(ccd_coefficient);
  const cc = parseBrazilianNumber(cc_coefficient);

  const outboundValue = distanceValue * ccd + cc;

  const emptyReturnValue = 0;

  const freightFloorTotal = outboundValue + emptyReturnValue;

  const cargoTypes = cargo_type && cargo_type.length > 0 ? Array.from(new Set(cargo_type)).join(', ') : null;

  const axles =
    vehicles && vehicles.length > 0
      ? vehicles.reduce((total, item) => total + parseBrazilianNumber(item.EIXO), 0)
      : null;

  return (
    <>
      <Divider title='Dados do Frete' />

      <Row>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StaticField label='Origem' value={renderValue(origin)} />
          </Grid>
          <Grid item xs={6}>
            <StaticField label='Destino' value={renderValue(destination)} />
          </Grid>
        </Grid>
      </Row>

      <Row>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <StaticField label='Distância (Km)' value={renderValue(distance)} />
          </Grid>
          <Grid item xs={4}>
            <StaticField label='Data de Emissão do Documento' value={formatDocumentDate(document_date)} />
          </Grid>
          <Grid item xs={4}>
            <StaticField label='Classificação' value={renderValue(classification)} />
          </Grid>
        </Grid>
      </Row>

      <Row>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StaticField label='Tipo de Contratação' value={renderValue(contract_type)} />
          </Grid>
          <Grid item xs={6}>
            <StaticField label='Tipos de Carga' value={renderValue(cargoTypes)} />
          </Grid>
        </Grid>
      </Row>

      <Divider title='Placas / Veículos' />

      {vehicles && vehicles.length > 0 ? (
        <VehiclesTable>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Placa</TableCell>
                <TableCell>Marca</TableCell>
                <TableCell>Modelo</TableCell>
                <TableCell>Eixos</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicles.map(item => (
                <TableRow key={`${item.PLACA}-${item.MARCA}-${item.MODELO}`}>
                  <TableCell>{renderValue(item.PLACA)}</TableCell>
                  <TableCell>{renderValue(item.MARCA)}</TableCell>
                  <TableCell>{renderValue(item.MODELO)}</TableCell>
                  <TableCell>{renderValue(item.EIXO)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </VehiclesTable>
      ) : (
        <Row>
          <StaticField label='Placa Principal' value={renderValue(vehicle)} />
        </Row>
      )}

      <SectionSpacer>
        <Divider title='Base cálculo do Piso Mínimo de Frete' />
      </SectionSpacer>

      <CalcCard>
        <p className='summary-title'>Resumo do cálculo</p>
        <CalcRow>
          <span>Distância da viagem</span>
          <strong>{distance ? `${distance} km` : EMPTY_FIELD_VALUE}</strong>
        </CalcRow>
        <CalcRow>
          <span>Quantidade total de eixos</span>
          <strong>{axles || EMPTY_FIELD_VALUE}</strong>
        </CalcRow>
        <CalcRow>
          <span>Coeficiente de Custo de Deslocamento (CCD)</span>
          <strong>{ccd_coefficient ? `${ccd_coefficient}` : EMPTY_FIELD_VALUE}</strong>
        </CalcRow>
        <CalcRow>
          <span>Coeficiente de Custo de Carga e Descarga (CC)</span>
          <strong>{cc_coefficient ? `${cc_coefficient}` : EMPTY_FIELD_VALUE}</strong>
        </CalcRow>
        <CalcRow className='total'>
          <span>Piso Mínimo de Frete Auto </span>
          <strong>{formatCurrency(freightFloorTotal, true)}</strong>
        </CalcRow>
      </CalcCard>
      <NoteCard>
        <p className='summary-note'>
          * Esses três campos (distância, CCD e CC) formam a base do piso mínimo de frete definido pela ANTT.
        </p>
        <p className='summary-note'>* Informações calculadas com base no auto da infração</p>
        <p className='summary-note'>* O cálculo segue a fórmula: (Distância × CCD) + CC.</p>
      </NoteCard>
    </>
  );
}

export default FreightFloor;
